import {createSlice} from "@reduxjs/toolkit";
import {currentProjectActions} from "./currentProject.js";
import {usersActions} from "./users.js";
import {fetchWithMessages} from "@shared/utils/api/fetchWithMessages";
import {SSO_HINT_COOKIE_NAME} from "@shared/utils/authConstants";
import {projectsActions} from "./projects";
import {viewActions} from "./view";
import {registrationsActions} from "./registrations";
import {sessionsActions} from "./sessions";
import i18n, {t} from "i18next";
import {changeSentryUser} from "@sfs/services/sentry";
import {safeLocalStorage} from "@sfs/utils/localStorageUtilities";
import {readCookie} from "@sfs/utils/cookiesUtilities";
import {changePosthogUser} from "@sfs/config/PostHog";
import {AppMethods} from "@sfs/hooks/useThemedAppMethods";

export const currentUserSlice = createSlice({
  name: "currentUser",
  initialState: {
    user: {},
    authenticatedUser: {},
    connected: undefined,
    connectionNotice: undefined,
    pendingSignupEmail: undefined,
    pendingSignupToken: undefined,
    dirty: false,
  },
  reducers: {
    changeLogin: (state, action) => {
      state.user.email = action.payload;
    },
    changeLastName: (state, action) => {
      state.user.lastName = action.payload;
    },
    changeFirstName: (state, action) => {
      state.user.firstName = action.payload;
    },
    changeUser: (state, action) => {
      state.user = action.payload;
    },
    changeAuthenticatedUser: (state, action) => {
      state.authenticatedUser = action.payload;
      changeSentryUser(state.authenticatedUser);
      changePosthogUser(state.authenticatedUser);
    },
    changeConnected: (state, action) => {
      state.connected = action.payload;
    },
    changeConnectionNotice: (state, action) => {
      state.connectionNotice = action.payload;
    },
    changePendingSignup: (state, action) => {
      state.pendingSignupEmail = action.payload.email;
      state.pendingSignupToken = action.payload.token;
    },
    reset: (state, action) => {
      state.user = {};
      state.authenticatedUser = {};
      changeSentryUser();
      changePosthogUser();
      state.connected = false;
      state.pendingSignupEmail = undefined;
      state.pendingSignupToken = undefined;
    },
    setDirty: (state, action) => {
      state.dirty = action.payload;
    },
  },
});

// fetchWithMessages rejects with `.status` set; 4xx = the request itself was refused (invalid
// link/code/token), anything else (5xx, a bug) is worth retrying.
const isClientError = (error) => error?.status >= 400 && error?.status < 500;

// Store the session JWT; connected=undefined makes the app reload the user with it.
const startSession = (dispatch, jwtToken) => {
  safeLocalStorage.setItem("token", jwtToken);
  dispatch(projectsActions.clean());
  dispatch(currentProjectActions.cleanProject());
  dispatch(currentUserActions.changeConnected(undefined));
};

const asyncActions = {
  // Claims an invited account (invitation-token flow of the SignUp page) and logs the user in.
  signUp:
    ({invitationToken, email, firstName, lastName}) =>
    async (dispatch) => {
      try {
        const data = await fetchWithMessages(
          "users",
          {
            noAuthNeeded: true,
            method: "POST",
            body: {invitationToken, email, firstName, lastName, locale: i18n.language},
          },
          // The API answers every bad invitation (unknown, already used, wrong email) with a 400.
          {statusMessages: {400: t("common:connectionPage.invitationTokenInvalid")}}
        );
        // An invitation claim always returns a session. No JWT (or a swallowed network
        // failure, which resolves undefined and was already toasted) means nothing to log in with.
        if (!data?.jwt_token) return false;
        startSession(dispatch, data.jwt_token);
        return {done: true, redirect: data.redirect};
      } catch {
        return false; // fetchWithMessages already toasted the HTTP error
      }
    },
  sendLoginLink: (redirect, emailOverride) => async (dispatch, getState) => {
    const state = getState();
    const email = emailOverride || state.currentUser.user.email;
    try {
      const data = await fetchWithMessages(
        "auth/sendLoginLink",
        {
          noAuthNeeded: true,
          method: "POST",
          body: {email, front: "inscription-front", redirect, lng: i18n.language},
        },
        {
          statusMessages: {
            // Two 429s exist on this route: the 15s resend cooldown and the
            // 6/hour flood limit, told apart by the body's machine-readable
            // `code`. The cooldown gets the localized wait message; the
            // hourly limit (and any other 429) gets the generic send error.
            429: {
              type: "error",
              message: (messageFromServer) => {
                let code = null;
                try {
                  code = JSON.parse(messageFromServer)?.code;
                } catch {
                  // Plain-text body: treat as the generic case.
                }
                return code === "resendCooldown"
                  ? t("common:connectionPage.magicLink.waitBeforeResending")
                  : t("common:connectionPage.magicLink.linkSendError");
              },
              duration: 10,
            },
          },
          // Any other failure (400, 5xx...) keeps the generic send error.
          defaultErrorMessage: t("common:connectionPage.magicLink.linkSendError"),
        }
      );
      // A network failure resolves undefined and was already toasted.
      if (!data) return false;
      if (data.dev?.loginUrl) {
        window.location.href = data.dev.loginUrl; // dev fast path
        return true;
      }
      dispatch(currentUserActions.changeConnectionNotice(undefined));
      return true;
    } catch {
      return false; // fetchWithMessages already toasted the HTTP error
    }
  },
  loginWithMagicToken: (token) => async (dispatch) => {
    try {
      const data = await fetchWithMessages(
        "auth/loginWithLink",
        {noAuthNeeded: true, method: "POST", body: {token}},
        // 400/401 mean an invalid or expired link: MagicLinkRedeem's expired screen says so,
        // so no toast. Anything else (5xx...) is transient and gets the stock toast.
        {statusMessages: {400: null, 401: null}}
      );
      // Network failure: fetchWithMessages swallows fetch errors and resolves undefined
      // (throwError defaults to false) — that is transient, not an invalid token.
      if (!data) return {transient: true};
      if (data.signupRequired) return {signupRequired: true, email: data.email};
      startSession(dispatch, data.jwt_token);
      return {loggedIn: true, redirect: data.redirect};
    } catch (error) {
      // 4xx means the token is genuinely invalid/expired; anything else (5xx) is transient.
      return isClientError(error) ? {invalid: true} : {transient: true};
    }
  },
  loginWithCode: (code, redirect) => async (dispatch, getState) => {
    const email = getState().currentUser.user.email;
    try {
      const data = await fetchWithMessages(
        "auth/loginWithCode",
        {noAuthNeeded: true, method: "POST", body: {email, code, redirect}},
        // The API answers a wrong, expired, used up or malformed code with a 400/401.
        {
          statusMessages: {
            400: t("common:connectionPage.magicLink.codeInvalid"),
            401: t("common:connectionPage.magicLink.codeInvalid"),
          },
        }
      );
      // Network failure: fetchWithMessages swallows fetch errors, toasts, and resolves undefined.
      if (!data) return {transient: true};
      if (data.signupRequired) return {signupRequired: true, signupToken: data.signupToken};
      startSession(dispatch, data.jwt_token);
      return {loggedIn: true, redirect: data.redirect};
    } catch (error) {
      // Any 4xx: wrong, expired, used up or malformed code. Anything else (5xx) is transient.
      return isClientError(error) ? {invalid: true} : {transient: true};
    }
  },
  completePasswordlessSignup:
    ({firstName, lastName}) =>
    async (dispatch, getState) => {
      const state = getState();
      try {
        const data = await fetchWithMessages(
          "users",
          {
            noAuthNeeded: true,
            method: "POST",
            body: {
              email: state.currentUser.pendingSignupEmail,
              firstName,
              lastName,
              locale: i18n.language, // schema default is "en"; user doc has no locale yet
              signupToken: state.currentUser.pendingSignupToken,
            },
          },
          {statusMessages: {400: t("common:defaultErrorMessages.error")}}
        );
        // A swallowed network failure resolves undefined and was already toasted.
        if (!data) return false;
        // a 201 without a JWT means the account already exists and is verified: not an HTTP
        // error, so fetchWithMessages has nothing to show.
        if (!data.jwt_token) {
          AppMethods.message.error(t("common:connectionPage.magicLink.linkExpiredBody"));
          return false;
        }
        startSession(dispatch, data.jwt_token);
        return {done: true, redirect: data.redirect};
      } catch {
        return false; // fetchWithMessages already toasted the HTTP error
      }
    },
  logOut: () => async (dispatch, getState) => {
    dispatch(currentProjectActions.cleanProject());
    dispatch(viewActions.setSearchParams({}));
    dispatch(projectsActions.cleanProjectList());
    // Fire-and-forget: must run while the XSRF token is still stored so the
    // request carries the double-submit header. No status expectation.
    fetchWithMessages("auth/logout", {method: "POST"}).catch(() => {});
    safeLocalStorage.removeItem("token");
    safeLocalStorage.removeItem("xsrfToken");
    safeLocalStorage.removeItem("x-connected-as-user");
    dispatch(currentUserActions.reset());
    dispatch(registrationsActions.setCurrent(undefined));
    dispatch(registrationsActions.setAuthenticated(undefined));
  },
  refreshAuthTokens: () => async (dispatch, getState) => {
    const state = getState();

    // Clean the connected-as-user token if there is no impersonated user
    // for the moment (ie. both current and authenticated users are equal)
    if (
      state.currentUser.user._id === state.currentUser.authenticatedUser._id &&
      safeLocalStorage.getItem("x-connected-as-user")?.length > 0
    ) {
      safeLocalStorage.removeItem("x-connected-as-user");
    }

    // With no local token we still probe: an SSO cookie on the parent domain
    // can bootstrap a session on this subdomain, and a 401 then must stay
    // silent (anonymous visitor, not an expiry). The sso_hint cookie lets an
    // anonymous visitor skip the probe entirely: no hint → guaranteed 401.
    const hadToken = !!safeLocalStorage.getItem("token");
    if (!hadToken && readCookie(SSO_HINT_COOKIE_NAME) === undefined) {
      await dispatch(currentUserActions.changeConnected(false));
      return;
    }
    try {
      const data = await fetchWithMessages(
        "auth/refreshAuthTokens",
        {method: "GET"},
        {
          statusMessages: {
            // null entry = fetchWithMessages skips the message entirely
            401: hadToken
              ? {type: "info", message: t("users:messages.pleaseReconnectToYourAccount")}
              : null,
          },
        }
      );

      // Change connection status and store the connection token
      safeLocalStorage.setItem("token", data.jwt_token);
      if (data.xsrf_token) safeLocalStorage.setItem("xsrfToken", data.xsrf_token);

      // Migration bridge: the API issued the auth cookie from our header JWT
      // (cookieIssued), so the cookie is now the primary credential and we can
      // drop the localStorage copy. Only on this explicit flag: a plain 200
      // from an older API must never log the user out of header auth.
      if (data.cookieIssued === true) {
        safeLocalStorage.removeItem("token");
      }

      await dispatch(currentUserActions.changeConnected(true));

      // Change the connected currentUser and real authenticatedUser
      await dispatch(currentUserActions.changeUser(data.user));
      await dispatch(usersActions.changeEditing(data.authenticatedUser));
      await dispatch(currentUserActions.changeAuthenticatedUser(data.authenticatedUser));
    } catch (e) {
      // Only a 401 proves the session is gone: drop the stale xsrfToken then,
      // it must not be sent again. Other errors (network, 5xx) leave it in
      // place so a transient failure doesn't log the user out for good.
      if (e?.status === 401) safeLocalStorage.removeItem("xsrfToken");
      await dispatch(currentUserActions.changeConnected(false));
    }
  },
  changeConnectedAsUser: (newConnectedAsUser) => async (dispatch, getState) => {
    const state = getState();
    const currentUser = state.currentUser.user;

    // If newConnectedAsUser is unset, reset to the original authenticated user
    if (!newConnectedAsUser) newConnectedAsUser = state.currentUser.authenticatedUser;

    // If the new user id is different from the connectedAsUser, then make the changes
    if (newConnectedAsUser._id !== currentUser._id) {
      await dispatch(currentUserActions.setDirty(true));

      await dispatch(registrationsActions.setCurrent(undefined));
      await dispatch(currentUserActions.changeUser(newConnectedAsUser));
      safeLocalStorage.setItem("x-connected-as-user", newConnectedAsUser._id);

      await dispatch(currentUserActions.refreshAuthTokens());
      await dispatch(registrationsActions.loadCurrent());
      await dispatch(sessionsActions.changeListFiltered([]));

      dispatch(currentUserActions.setDirty(false));

      // Reinitialize the sessions list with data relative to the impersonated user
      dispatch(sessionsActions.initList({list: [], project: undefined}));
      dispatch(sessionsActions.loadList());
    }
  },
  deleteAccount: () => async (dispatch, getState) => {
    const state = getState();
    await fetchWithMessages(
      `users/${state.currentUser.authenticatedUser._id}`,
      {method: "DELETE"},
      {statusMessages: {200: t("users:messages.accountSuccessfullyDeleted")}}
    );
    dispatch(currentUserActions.logOut());
  },
};

export const currentUserSelectors = {
  selectUser: (state) => state.currentUser.user,
  selectAuthenticatedUser: (state) => state.currentUser.authenticatedUser,
  selectConnected: (state) => state.currentUser.connected,
  selectConnectionNotice: (state) => state.currentUser.connectionNotice,
  selectDirty: (state) => state.currentUser.dirty,
};

export const currentUserReducer = currentUserSlice.reducer;

export const currentUserActions = {
  ...currentUserSlice.actions,
  ...asyncActions,
};
